import clsx from "clsx";

/**
 * Props interface for the PageLoading component.
 */
interface PageLoadingProps {
  /** Message shown below the spinner */
  message: string;
  /** Tailwind border colour class(es) for the spinner (e.g. border-pink-600, border-lavender-400). Default: border-pink-600 */
  className?: string;
  /** When true, fills the viewport (min-h-screen, no background). When false, fills layout area (h-full bg-gray-50). Default: false */
  fullScreen?: boolean;
}

const CONTAINER_CLASS = "flex items-center justify-center";
const IN_LAYOUT_CLASS = `${CONTAINER_CLASS} h-full bg-gray-50`;
const FULL_SCREEN_CLASS = `${CONTAINER_CLASS} min-h-screen`;

/**
 * Full-page or full-area loading state with spinner and message.
 * Use for route-level loading (profile, matches, discovery) or auth checks.
 *
 * @example
 * ```tsx
 * <PageLoading message="Loading profile..." />
 * <PageLoading message="Loading your matches..." className="border-lavender-400" />
 * <PageLoading message="Checking authentication..." fullScreen />
 * ```
 */
export function PageLoading({ message, className = "border-pink-600", fullScreen = false }: PageLoadingProps) {
  return (
    <div className={fullScreen ? FULL_SCREEN_CLASS : IN_LAYOUT_CLASS}>
      <div className="text-center">
        <div className={clsx("animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4", className)} aria-hidden />
        <p className="text-gray-600">{message}</p>
      </div>
    </div>
  );
}
